/**
 * Лайтбокс для защищённых файлов.
 *
 * Обычный `Lightbox` получает готовый src и рисует его `<img>`. Файлы черновика
 * лежат за гейтвеем и требуют токен — такой запрос вернул бы 401, поэтому
 * адрес сначала превращается в blob тем же загрузчиком, что и миниатюры.
 *
 * Загружается только открытый кадр: тянуть все два десятка визуалов ради
 * одного просмотра значит выесть бюджет медиа-запросов на ровном месте.
 * Соседние подгружаются при переключении — за это отвечает сам `Lightbox`,
 * меняя индекс.
 */

import React from 'react'
import { Lightbox } from '../../../components/ui/Lightbox'
import { useAuthorizedMediaUrl } from '../../../shared/media/authorizedMedia'

interface AuthorizedLightboxProps {
  isOpen: boolean
  /** Исходные адреса, в порядке отправки на площадку. */
  sources: string[]
  index: number
  onIndexChange: (index: number) => void
  onClose: () => void
  /** Подпись кадра: по умолчанию «Визуал N». */
  label?: (index: number) => string
}

export const AuthorizedLightbox: React.FC<AuthorizedLightboxProps> = ({
  isOpen,
  sources,
  index,
  onIndexChange,
  onClose,
  label,
}) => {
  const active = isOpen ? sources[index] : undefined
  const { src, loading, error } = useAuthorizedMediaUrl(active)

  if (!isOpen || !active) return null

  /*
    Соседние кадры остаются заглушками до перехода на них: `Lightbox` считает
    длину массива, поэтому стрелки и счётчик «N из M» работают как обычно, а
    вес тянется только за открытым кадром.
  */
  const items = sources.map((source, position) => ({
    src: position === index ? src || '' : '',
    label: label ? label(position) : `Визуал ${position + 1}`,
  }))

  return (
    <>
      <Lightbox
        isOpen={isOpen}
        items={items}
        index={index}
        onIndexChange={onIndexChange}
        onClose={onClose}
      />
      {(loading || error) && (
        <div className="pointer-events-none fixed inset-x-0 bottom-8 z-[80] text-center text-sm text-white/80">
          {error ? 'Не удалось загрузить изображение' : 'Загрузка изображения…'}
        </div>
      )}
    </>
  )
}

export default AuthorizedLightbox
